import React, { useState, useEffect, RefObject, useRef } from "react";
import { SustSheet } from "./SustSheet";
import { useIsMobile } from "../hooks";
import { SheetContent } from "./SheetContent";
import { MetricEmitterProvider } from "../contexts/MetricEmitterContext";
import type { AppSettings, ClimatePledgeFriendlyFeatures } from "../types";
import type { SustainabilityMetricEmitter } from "../../../../sdk/metrics";
import cssContent from "../../../../assets/styles/sustainability/ComponentUI.css?raw";

export interface ResponsiveSheetProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  cpfData: ClimatePledgeFriendlyFeatures;
  className?: string;
  focusManagement?: ReturnType<typeof import("../hooks").useFocusManagement>;
  badgeRef?: RefObject<HTMLElement>;
  metricEmitter: SustainabilityMetricEmitter;
}

export const ResponsiveSheet: React.FC<ResponsiveSheetProps> = ({
  isOpen,
  onClose,
  settings,
  cpfData,
  className = "",
  focusManagement,
  badgeRef,
  metricEmitter,
}) => {
  const isMobile = useIsMobile();
  const [isClient, setIsClient] = useState(false);
  const [sheetHeight, setSheetHeight] = useState<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Reset sheet height when sheet closes or viewport type changes
  useEffect(() => {
    if (!isOpen) {
      setSheetHeight(null);
    }
  }, [isOpen]);

  // Reset height when switching between mobile and desktop
  useEffect(() => {
    setSheetHeight(null);
  }, [isMobile]);

  // Measure sheet height on mobile using ResizeObserver
  useEffect(() => {
    if (!isOpen || !isMobile || !sheetRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setSheetHeight(height);
        }
      }
    });

    observer.observe(sheetRef.current);

    return () => observer.disconnect();
  }, [isOpen, isMobile]);

  // Set the badge trigger element when sheet opens
  useEffect(() => {
    if (isOpen && focusManagement && badgeRef?.current) {
      focusManagement.setSheetTriggerElement(badgeRef.current);
    }
  }, [isOpen, focusManagement, badgeRef]);

  // Handle close with focus restoration
  const handleClose = () => {
    onClose();
    // Restore focus to badge button after sheet closes
    if (focusManagement) {
      focusManagement.restoreFocusToSheetTrigger();
    }
  };

  if (!isClient) {
    return null;
  }

  // Determine sheet side based on device type
  const sheetSide = isMobile ? "bottom" : "end";

  const colorMode = settings["color-mode"];
  const themeClass = `sustainability-theme-${colorMode}`;
  const scopedClassName = `responsive-sheet ${isMobile ? "responsive-sheet--mobile" : "responsive-sheet--desktop"} ${themeClass} ${className}`;

  const sheetStyle: React.CSSProperties | undefined =
    isMobile && sheetHeight !== null
      ? { height: `${sheetHeight}px` }
      : undefined;

  return (
    <SustSheet
      open={isOpen}
      onClose={handleClose}
      side={sheetSide}
      className={scopedClassName}
      cssContent={cssContent}
      style={sheetStyle}
      sheetRef={sheetRef}
    >
      <MetricEmitterProvider value={metricEmitter}>
        <SheetContent
          settings={settings}
          cpfData={cpfData}
          onClose={handleClose}
          focusManagement={focusManagement}
          isOpen={isOpen}
        />
      </MetricEmitterProvider>
    </SustSheet>
  );
};
